// src/components/SearchOmnibar.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MeiliSearch } from 'meilisearch';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, MapPin, Store, Zap, Flame, Building2 } from 'lucide-react';

// 🚨 SÉCURITÉ ANTI-PAGE BLANCHE
let searchClient: MeiliSearch | null = null;

try {
  const host = import.meta.env.VITE_MEILISEARCH_HOST;
  const apiKey = import.meta.env.VITE_MEILISEARCH_SEARCH_KEY;
  
  if (host && apiKey) {
    searchClient = new MeiliSearch({ host, apiKey });
  } else {
    console.warn("⚠️ Meilisearch: Variables VITE_MEILISEARCH_HOST ou VITE_MEILISEARCH_SEARCH_KEY manquantes. Recherche désactivée.");
  }
} catch (e) {
  console.error("❌ Erreur critique lors de l'initialisation de Meilisearch :", e);
}

export default function SearchOmnibar({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
      setQuery('');
      setResults([]);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        isOpen ? onClose() : null; 
      }
      if (e.key === 'Escape' && isOpen) onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    const fetchSearch = async () => {
      if (!searchClient) return; // Sécurité si le client n'a pas pu s'initialiser
      
      setLoading(true);
      try {
        const res = await searchClient.index('forfeo_explore_global').search(query, {
          limit: 8,
          attributesToHighlight: ['title', 'description']
        });
        setResults(res.hits);
      } catch (e) {
        console.error("Erreur recherche:", e);
      } finally {
        setLoading(false);
      }
    };

    const delay = setTimeout(fetchSearch, 300);
    return () => clearTimeout(delay);
  }, [query]);

  const handleSelect = (route: string) => {
    onClose();
    navigate(route);
  };

  const getIconForType = (type: string) => {
    switch(type) {
      case 'businesses': return <Building2 size={16} className="text-blue-500" />;
      case 'boutique_forfaits': return <Store size={16} className="text-purple-500" />;
      case 'last_call_deals_public': return <Zap size={16} className="text-red-500" />;
      case 'forfeo_finds_public': return <Flame size={16} className="text-orange-500" />;
      default: return <Search size={16} className="text-slate-400" />;
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999] flex items-start justify-center pt-[10vh] px-4">
        <motion.div 
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" 
          onClick={onClose} 
        />
        
        <motion.div 
          initial={{ opacity: 0, y: -20, scale: 0.95 }} 
          animate={{ opacity: 1, y: 0, scale: 1 }} 
          exit={{ opacity: 0, y: -20, scale: 0.95 }}
          className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[80vh]"
        >
          <div className="flex items-center p-4 border-b border-slate-100 bg-slate-50/50">
            <Search className="text-slate-400 ml-2" size={24} />
            <input
              ref={inputRef}
              type="text"
              className="w-full bg-transparent border-none px-4 py-3 text-xl font-black text-slate-900 outline-none placeholder-slate-300"
              placeholder="Chercher un partenaire, un hôtel, une offre..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {loading && <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-2"></div>}
            <button onClick={onClose} className="p-2 bg-slate-100 hover:bg-slate-200 rounded-full text-slate-500 transition">
              <X size={20} />
            </button>
          </div>

          <div className="overflow-y-auto p-2">
            {!query && (
              <div className="p-8 text-center text-slate-400 font-bold">
                Appuyez sur <kbd className="bg-slate-100 px-2 py-1 rounded-md border border-slate-200 text-xs">Cmd/Ctrl + K</kbd> pour chercher rapidement n'importe où.
              </div>
            )}
            
            {query && results.length === 0 && !loading && (
              <div className="p-8 text-center text-slate-500 font-bold">
                Aucun résultat pour "{query}" 🏜️
              </div>
            )}

            {results.map((hit) => (
              <div 
                key={hit.id} 
                onClick={() => handleSelect(hit.route)}
                className="flex items-center gap-4 p-4 hover:bg-slate-50 cursor-pointer rounded-2xl transition group border border-transparent hover:border-slate-100"
              >
                <div className="w-12 h-12 rounded-xl bg-slate-100 overflow-hidden shrink-0 flex items-center justify-center">
                  {hit.image_url ? (
                    <img src={hit.image_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    getIconForType(hit.type)
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-black text-slate-900 truncate" dangerouslySetInnerHTML={{ __html: hit._formatted?.title || hit.title }} />
                  <p className="text-xs text-slate-500 truncate" dangerouslySetInnerHTML={{ __html: hit._formatted?.description || hit.subtitle }} />
                </div>
                {hit.location && (
                  <div className="text-[10px] font-bold text-slate-400 flex items-center gap-1 shrink-0 bg-slate-100 px-2 py-1 rounded-md">
                    <MapPin size={10} /> {hit.location}
                  </div>
                )}
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
